import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateBoth } from '../utils/dates';
import { diffWords, diffStats } from '../utils/textDiff';
import ReviewItems from '../components/ReviewItems';

const API = process.env.REACT_APP_API || '/api';

// حالات المرجع — المطابق للمصحف وحده يُعرض معتمَداً، وما عداه يُنتظر نظر مراجع
const REF_STATUS_AR = {
  verified: 'معتمَد',
  review: 'يحتاج مراجعة',
  unverified: 'غير مخرَّج',
};

// مستويات ثقة المزوّد في المقطع — الضعيف هو ما يستمع إليه المحرّر
const CONF_TONE = {
  good:    { bg: 'transparent',         label: 'جيّدة' },
  fair:    { bg: 'var(--c-gold-soft)',  label: 'متوسّطة' },
  weak:    { bg: 'var(--c-coral-soft)', label: 'ضعيفة' },
  unknown: { bg: 'transparent',         label: 'مجهولة' },
};

// الفائدة تنتقل: مقترحة ← معتمدة ← منشورة. والاعتماد بشريّ بحكم القاعدة.
const BENEFIT_STATUS = {
  suggested: { label: 'مقترحة', badge: 'waiting_approval', next: 'approved',  nextLabel: '✓ اعتمد' },
  approved:  { label: 'معتمدة', badge: 'approved',         next: 'published', nextLabel: '📤 نُشرت' },
  published: { label: 'منشورة', badge: 'completed',        next: null,        nextLabel: null },
};

const REF_TONE = {
  verified:   { bg: 'var(--c-success-soft)', line: 'var(--c-success)', ink: 'var(--c-success)', badge: 'completed' },
  review:     { bg: 'var(--c-coral-soft)',   line: 'var(--c-coral)',   ink: 'var(--c-coral)',   badge: 'revision' },
  unverified: { bg: 'var(--c-gold-soft)',    line: 'var(--c-gold)',    ink: 'var(--c-gold-ink)', badge: 'waiting_approval' },
};

export default function Transcription() {
  const { api, apiUpload, user, token } = useAuth();
  const toast = useToast();
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [title, setTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [inputMode, setInputMode] = useState('file');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [directUrl, setDirectUrl] = useState('');
  const [directLoading, setDirectLoading] = useState(false);
  const [ytLoading, setYtLoading] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaLoading, setMediaLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pickClient, setPickClient] = useState('');
  const [pickProject, setPickProject] = useState('');
  const [batchProgress, setBatchProgress] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [editingText, setEditingText] = useState(false);
  const audioRef = useRef(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const activeWordRef = useRef(null);

  const load = (qs = '') => api(`/transcriptions${qs}`).then(setTranscriptions).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); api('/clients').then(setClients).catch(() => {}); api('/projects').then(setProjects).catch(() => {}); }, []);

  // Re-load when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterClient) params.set('client_id', filterClient);
    const qs = params.toString();
    load(qs ? `?${qs}` : '');
  }, [filterClient]); // eslint-disable-line

  const onSearchKey = (e) => {
    if (e.key === 'Enter') {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterClient) params.set('client_id', filterClient);
      const qs = params.toString();
      load(qs ? `?${qs}` : '');
    }
  };

  // polling on active jobs
  useEffect(() => {
    const hasActive = transcriptions.some(t => t.status === 'processing' || t.status === 'queued');
    if (!hasActive) return;
    const timer = setTimeout(() => load(), 4000);
    return () => clearTimeout(timer);
  }, [transcriptions]);

  /**
   * الروابط الموقوتة (الفائدة ونتيجة البحث) تصل بـ ?id=&t= — وبدون هذا
   * تفتح الصفحة على القائمة، فيصير «رابط تشغيل» رابطاً إلى ملفّ من ساعتين.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = parseInt(params.get('id'), 10);
    if (!id) return;

    const at = parseFloat(params.get('t'));
    if (Number.isFinite(at) && at > 0) pendingSeek.current = { id, at };
    selectTr({ id }).catch(() => toast.error('تعذّر فتح التفريغ المطلوب'));
    // مرّة واحدة عند الفتح — التنقّل بعدها بالقائمة
  }, []); // eslint-disable-line

  // بعد تحميل الوسيط لا قبله: ضبط currentTime على عنصر بلا مدّة يُهمَل.
  // والقفزة موسومة بصاحبها — قفزةٌ لم تُستهلك كانت تنتقل إلى الدرس التالي.
  const applyPendingSeek = (id) => {
    const pending = pendingSeek.current;
    if (!pending || pending.id !== id || !audioRef.current) return;
    pendingSeek.current = null;
    audioRef.current.currentTime = pending.at;
    setCurrentTime(pending.at);
  };

  // refresh selected while processing — والتحليل يبدأ بعد «مكتمل» فلا
  // تكفي حالة المعالجة وحدها: إعادة تحميل الصفحة كانت تترك الشريط عالقاً
  useEffect(() => {
    if (!selected) return;
    const busy = selected.status === 'processing' || selected.status === 'queued'
      || selected.analysis_state === 'running';
    if (!busy) return;
    const timer = setTimeout(() => selectTr({ id: selected.id }).catch(() => {}), 4000);
    return () => clearTimeout(timer);
  }, [selected]);

  const words = useMemo(() => {
    if (!selected?.words_json) return null;
    try { return JSON.parse(selected.words_json); } catch { return null; }
  }, [selected]);

  // Align each word of formatted_text with a timestamp from words_json
  // by SEQUENTIAL POSITION (not text equality — repeated words must each
  // map to their own occurrence's timestamp).
  const alignedTokens = useMemo(() => {
    const text = selected?.formatted_text || selected?.raw_text || '';
    if (!text) return [];

    const parts = text.split(/(\s+)/);
    const wordCount = parts.filter(p => p.trim()).length;
    const raw = words || [];

    let wordPos = 0;
    return parts.map((part) => {
      if (!part.trim()) return { text: part, isWhitespace: true };

      const rawIdx = raw.length
        ? Math.min(raw.length - 1, Math.floor((wordPos / Math.max(1, wordCount)) * raw.length))
        : -1;
      const rw = rawIdx >= 0 ? raw[rawIdx] : null;
      wordPos++;

      return {
        text: part,
        isWhitespace: false,
        start: rw?.start ?? null,
        end: rw?.end ?? null,
      };
    });
  }, [selected, words]);

  // الامتداد يأتي من الخادم: الرابط الموقَّع يحمل المسار مُرمَّزاً في استعلامه
  const isVideo = useMemo(
    () => ['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv'].includes(selected?.audio_ext || ''),
    [selected],
  );

  useEffect(() => {
    if (activeWordRef.current) {
      activeWordRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentTime]);

  // ---------- Upload (single + batch) ----------
  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('audio', file);
    fd.append('title', file.name.replace(/\.[^/.]+$/, ''));
    if (pickClient)  fd.append('client_id', pickClient);
    if (pickProject) fd.append('project_id', pickProject);
    const result = await apiUpload('/transcriptions', fd, (p) => setUploadPct(p));
    await api(`/transcriptions/${result.id}/transcribe`, { method: 'POST' });
    return result;
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    try {
      setProcessing(true);
      if (files.length === 1) {
        const file = files[0];
        const fd = new FormData();
        fd.append('audio', file);
        fd.append('title', title || file.name);
        if (pickClient)  fd.append('client_id', pickClient);
        if (pickProject) fd.append('project_id', pickProject);
        setUploadPct(0);
        const result = await apiUpload('/transcriptions', fd, (p) => setUploadPct(p));
        setUploadPct(100);
        await api(`/transcriptions/${result.id}/transcribe`, { method: 'POST' });
        setTitle('');
        await load();
        try { await selectTr({ id: result.id }); } catch {}
      } else {
        setBatchProgress({ done: 0, total: files.length });
        for (let i = 0; i < files.length; i++) {
          try {
            await uploadOne(files[i]);
          } catch (err) {
            toast.error(`فشل ملف "${files[i].name}": ${err.message}`);
          }
          setBatchProgress({ done: i + 1, total: files.length });
        }
        toast.success(`اكتمل رفع ${files.length} ملف`);
        setBatchProgress(null);
      }
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
      setUploadPct(null);
    }
  };

  const handleYoutube = async () => {
    if (!youtubeUrl.trim()) return;
    setYtLoading(true);
    try {
      const body = { youtube_url: youtubeUrl, title: title || youtubeUrl };
      if (pickClient)  body.client_id = pickClient;
      if (pickProject) body.project_id = pickProject;
      await api('/transcriptions', { method: 'POST', body: JSON.stringify(body) });
      setYoutubeUrl(''); setTitle('');
      toast.success('بدأ الجلب من يوتيوب — يتابع الخادم بقيّة الخطوات');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setYtLoading(false);
    }
  };

  const handleMediaUrl = async () => {
    if (!mediaUrl.trim()) return;
    setMediaLoading(true);
    try {
      const body = { media_url: mediaUrl.trim(), title: title || mediaUrl };
      if (pickClient)  body.client_id = pickClient;
      if (pickProject) body.project_id = pickProject;
      await api('/transcriptions', { method: 'POST', body: JSON.stringify(body) });
      setMediaUrl(''); setTitle('');
      toast.success('بدأ التحميل عبر Cobalt — يتابع الخادم بقيّة الخطوات');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleDirectUrl = async () => {
    if (!directUrl.trim()) return;
    setDirectLoading(true);
    try {
      const body = { direct_url: directUrl.trim(), title: title || directUrl };
      if (pickClient)  body.client_id = pickClient;
      if (pickProject) body.project_id = pickProject;
      await api('/transcriptions', { method: 'POST', body: JSON.stringify(body) });
      setDirectUrl(''); setTitle('');
      toast.success('بدأ التحميل — يتابع الخادم بقيّة الخطوات');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDirectLoading(false);
    }
  };

  const selectTr = async (tr) => {
    const full = await api(`/transcriptions/${tr.id}`);
    setSelected(full);
    setEditText(full.formatted_text || full.raw_text || '');
    setCurrentTime(pendingSeek.current?.id === tr.id ? pendingSeek.current.at : 0);
    setIsPlaying(false);
    setEditingText(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause(); else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const seekTo = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const seekToWord = (start) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = start;
    if (!isPlaying) { audioRef.current.play(); setIsPlaying(true); }
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const saveText = async () => {
    await api(`/transcriptions/${selected.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        formatted_text: editText,
        client_id: selected.client_id,
        project_id: selected.project_id,
      }),
    });
    setSelected(s => s ? { ...s, formatted_text: editText } : s);
    toast.success('تم الحفظ');
    load();
  };

  const updateMeta = async (changes) => {
    const id = selected.id;
    const before = Object.fromEntries(Object.keys(changes).map(k => [k, selected[k]]));
    setSelected(s => (s?.id === id ? { ...s, ...changes } : s));
    try {
      await api(`/transcriptions/${id}`, { method: 'PUT', body: JSON.stringify({ ...changes }) });
    } catch (err) {
      // بلا تراجع كان المرفوض يبقى معروضاً كأنه محفوظ حتى إعادة التحميل
      setSelected(s => (s?.id === id ? { ...s, ...before } : s));
      throw err;
    }
    load();
  };

  const retranscribe = async (id) => {
    setProcessing(true);
    try {
      await api(`/transcriptions/${id}/transcribe`, { method: 'POST' });
      load();
      if (selected?.id === id) selectTr({ id });
    } catch (err) { toast.error(err.message); }
    setProcessing(false);
  };

  const deleteTr = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('هل أنت متأكد من حذف هذا التفريغ؟')) return;
    try {
      await api(`/transcriptions/${id}`, { method: 'DELETE' });
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  // التنزيل لا يمرّ بـ api() لأنه يعيد blob لا JSON — لكنّه يحتاج نفس
  // الاعتماد: الدخول يصدر رمز Sanctum ولا ينشئ جلسة، فـ credentials
  // وحدها لا تحمل شيئاً والمسار يردّ 401.
  const exportFile = async (format) => {
    if (!selected) return;
    try {
      const res = await fetch(`${API}/transcriptions/${selected.id}/export/${format}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/octet-stream' },
      });
      if (!res.ok) {
        // هوية الشيخ الناقصة تُسمّى في الردّ — تُعرض كما هي لا «فشل التصدير»
        const msg = res.status === 401 ? null : await res.json().catch(() => null);
        throw new Error(res.status === 401 ? 'جلسة منتهية' : (msg?.error || 'فشل التصدير'));
      }

      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${selected.title || 'transcript'}.${format === 'site' ? 'html' : format}`;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) { toast.error(err.message); }
  };

  const refs = selected?.references_json && Array.isArray(selected.references_json) ? selected.references_json : [];

  const chapters = (() => {
    if (!selected?.chapters_json) return [];
    if (Array.isArray(selected.chapters_json)) return selected.chapters_json;
    try { return JSON.parse(selected.chapters_json) || []; } catch { return []; }
  })();

  const [summaryEdit, setSummaryEdit] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [chaptersBusy, setChaptersBusy] = useState(false);
  const [refsBusy, setRefsBusy] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [refReviewBusy, setRefReviewBusy] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [benefitCounts, setBenefitCounts] = useState({ suggested: 0, approved: 0, published: 0 });
  const [benefitsBusy, setBenefitsBusy] = useState(false);
  const [benefitEditing, setBenefitEditing] = useState(null);
  const canManageBenefits = user?.permissions?.includes('transcription.benefits');
  const [showConfidence, setShowConfidence] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishEdit, setPublishEdit] = useState(false);
  const [publishDraft, setPublishDraft] = useState(null);
  const [readableBusy, setReadableBusy] = useState(false);
  const [readableEdit, setReadableEdit] = useState(false);
  const [readableDraft, setReadableDraft] = useState('');
  const [publishTo, setPublishTo] = useState(null);
  const [publishSending, setPublishSending] = useState(false);
  const pendingSeek = useRef(null);
  const [links, setLinks] = useState([]);
  const [linksBusy, setLinksBusy] = useState(false);

  const segments = useMemo(() => {
    if (!selected?.segments_json) return [];
    try { return JSON.parse(selected.segments_json) || []; } catch { return []; }
  }, [selected?.segments_json]);

  const confidence = selected?.confidence || { weak: 0, fair: 0, total: 0, has_confidence: false };
  const weakSegments = useMemo(() => segments.filter(s => s.level === 'weak'), [segments]);
  const canReviewRefs = user?.permissions?.includes('transcription.review');
  const canPublish = user?.permissions?.includes('transcription.publish');

  // مقارنة الخام بالمنسّق: النصّان محفوظان معاً، لكن لا شيء كان يكشف ما
  // أحدثه التنسيق الآلي في كلام الشيخ. تُحسب عند الطلب فقط.
  const diffParts = useMemo(
    () => (showDiff ? diffWords(selected?.raw_text || '', selected?.formatted_text || '') : []),
    [showDiff, selected?.raw_text, selected?.formatted_text]
  );
  const diffCounts = useMemo(() => diffStats(diffParts), [diffParts]);

  useEffect(() => { setShowDiff(false); setShowConfidence(false); }, [selected?.id]);

  // ناتج مدقّق الخادم — هو الحَكَم في «هل غيّر التنسيق لفظ الشيخ»، والفرق
  // المرسوم تحته توضيح بصري له
  const [reviewOpen, setReviewOpen] = useState(false);
  const [formatCheck, setFormatCheck] = useState(null);
  const [corrections, setCorrections] = useState([]);
  useEffect(() => {
    if (!showDiff || !selected?.id) { setFormatCheck(null); setCorrections([]); return; }
    let live = true;
    api(`/transcriptions/${selected.id}/format-check`)
      .then(r => { if (live) setFormatCheck(r); })
      .catch(() => {});
    api(`/transcriptions/${selected.id}/corrections`)
      .then(r => { if (live) setCorrections(Array.isArray(r) ? r : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [showDiff, selected?.id, selected?.formatted_text, api]);

  const revertCorrection = async (c) => {
    try {
      await api(`/transcriptions/${selected.id}/corrections/${c.id}/revert`, { method: 'POST' });
      setSelected(await api(`/transcriptions/${selected.id}`));
      toast.success('عُكس التصحيح');
    } catch (err) { toast.error(err.message); }
  };

  useEffect(() => {
    setSummaryDraft(selected?.summary || '');
    setSummaryEdit(false);
  }, [selected?.id]);

  const fmtChapterTime = (sec) => {
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    return `${m}:${String(ss).padStart(2, '0')}`;
  };

  const saveSummary = async () => {
    setSummaryBusy(true);
    try {
      await api(`/transcriptions/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({ summary: summaryDraft }),
      });
      setSelected(s => ({ ...s, summary: summaryDraft }));
      setSummaryEdit(false);
      toast.success('تم حفظ الملخص');
    } catch (err) { toast.error(err.message); }
    setSummaryBusy(false);
  };

  // المتابعة تُلغى عند تغيير التفريغ أو مغادرة الصفحة: بدونها تظلّ تدور
  // ستّ دقائق ثم تكتب تفريغاً على تفريغ آخر يقرؤه المحرّر.
  //
  // والرمز لكل تشغيلة لا للتفريغ: طلبٌ يردّه الخادم بـ409 («التحليل جارٍ»)
  // كان يُنهي متابعةً جارية مشروعة، فيضيع خبر انتهائها.
  const analysisWatch = useRef(0);
  useEffect(() => {
    analysisWatch.current++;
    return () => { analysisWatch.current++; };
  }, [selected?.id]);

  /**
   * يرسل طلب تحليل ثم يتابع analysis_state حتى ينتهي. التحليل صار يغطّي
   * النصّ كلّه فدرس الساعتين عشرات النداءات — لا يُنتظر داخل الطلب.
   */
  const watchAnalysis = async (request, doneMessage, afterDone) => {
    const id = selected.id;

    // الرمز يُؤخذ قبل الطلب: تبديل التفريغ أثناء طيرانه يرفع العدّاد فتموت
    // هذه المتابعة — وأخذُه بعده كان يُحييها فتكتب نتيجة درس في مسوّدة آخر.
    // والطلب الفاشل يعيد العدّاد كما كان، فلا يُبطل متابعةً قائمة.
    const previous = analysisWatch.current;
    const token = ++analysisWatch.current;
    const alive = () => analysisWatch.current === token;

    try {
      await request(id);
    } catch (err) {
      if (analysisWatch.current === token) analysisWatch.current = previous;
      throw err;
    }
    if (!alive()) return;

    setSelected(s => (s?.id === id ? { ...s, analysis_state: 'running', analysis_error: null } : s));

    let misses = 0;
    for (let tries = 0; tries < 120; tries++) {
      await new Promise(r => setTimeout(r, 3000));
      if (!alive()) return;

      let fresh;
      try {
        fresh = await api(`/transcriptions/${id}`);
      } catch {
        // انقطاع لحظي على الاستضافة المشتركة لا يعني فشل التحليل
        if (++misses >= 5) { toast.warning('انقطعت متابعة التحليل — أعد فتح التفريغ بعد قليل.'); return; }
        continue;
      }
      misses = 0;
      if (!alive()) return;
      if (fresh.analysis_state === 'running') continue;

      setSelected(s => (s?.id === id ? fresh : s));
      setSummaryDraft(fresh.summary || '');
      if (afterDone) await afterDone(id);

      if (fresh.analysis_state === 'failed') toast.error(fresh.analysis_error || 'فشل التحليل');
      else toast.success(doneMessage);
      return;
    }

    toast.warning('التحليل ما زال جارياً — أعد فتح التفريغ بعد قليل.');
  };

  const runAnalysis = async (step, setBusy, doneMessage) => {
    setBusy(true);
    try {
      await watchAnalysis(
        id => api(`/transcriptions/${id}/regenerate-${step}`, { method: 'POST' }),
        doneMessage
      );
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const regenSummary = () => runAnalysis('summary', setSummaryBusy, 'تم توليد الملخص');
  const regenChapters = () => runAnalysis('chapters', setChaptersBusy, 'تم توليد الفصول');
  const regenReferences = () => runAnalysis('references', setRefsBusy, 'تم كشف المراجع');
  const regenPublish = () => runAnalysis('publish', setPublishBusy, 'جُهّزت حقول النشر');
  const regenReadable = () => runAnalysis('readable', setReadableBusy, 'تمّت النسخة المقروءة');
  const [formatBusy, setFormatBusy] = useState(false);
  const regenFormat = () => {
    if (!window.confirm('إعادة تنسيق المتن من الخام بالمدقّق؟ النصّ الحالي يُحفظ ويمكن استعادته.')) return;
    runAnalysis('format', setFormatBusy, 'أُعيد التنسيق');
  };
  const revertFormat = async () => {
    try {
      await api(`/transcriptions/${selected.id}/format/revert`, { method: 'POST' });
      setSelected(await api(`/transcriptions/${selected.id}`));
      toast.success('استُعيد النصّ السابق');
    } catch (err) { toast.error(err.message); }
  };

  /** البند ٢٢: ما سيُرسل وما يمنع إرساله يُقرأ قبل الضغط لا بعده. */
  const openPublish = async () => {
    try {
      setPublishTo(await api(`/transcriptions/${selected.id}/publish`));
    } catch (err) { toast.error(err.message); }
  };

  const sendToSite = async () => {
    setPublishSending(true);
    try {
      const res = await api(`/transcriptions/${selected.id}/publish`, { method: 'POST' });
      setSelected(s => (s?.id === selected.id ? { ...s, published_at: res.published.at, published_url: res.published.url } : s));
      setPublishTo(null);
      toast.success(res.published.url ? 'نُشر — افتح الرابط للمراجعة' : 'تم النشر');
    } catch (err) { toast.error(err.message); }
    finally { setPublishSending(false); }
  };

  /** الاقتراح لا يُفرض: يُحمَّل في مسوّدة يحرّرها المحرّر ثم يحفظ. */
  const openPublishEdit = () => {
    setPublishDraft({
      publish_title: selected.publish_title || '',
      publish_description: selected.publish_description || '',
      publish_intro: selected.publish_intro || '',
      tags: (selected.publish_tags || []).join('، '),
    });
    setPublishEdit(true);
  };

  const savePublish = async () => {
    setPublishBusy(true);
    try {
      await updateMeta({
        publish_title: publishDraft.publish_title,
        publish_description: publishDraft.publish_description,
        publish_intro: publishDraft.publish_intro,
        publish_tags: publishDraft.tags.split(/[،,\n]/).map(t => t.trim()).filter(Boolean),
      });
      setPublishEdit(false);
      toast.success('حُفظت حقول النشر');
    } catch (err) { toast.error(err.message); }
    finally { setPublishBusy(false); }
  };

  const saveReadable = async () => {
    setReadableBusy(true);
    try {
      await updateMeta({ readable_text: readableDraft });
      setReadableEdit(false);
      toast.success('حُفظت النسخة المقروءة');
    } catch (err) { toast.error(err.message); }
    finally { setReadableBusy(false); }
  };

  /** البند ٢٤: إحالات السلسلة — تُقرأ وتُعتمد أو تُرفض، ولا تُنشر بنفسها. */
  const loadLinks = useCallback(async (id) => {
    if (!id) { setLinks([]); return; }
    try {
      const res = await api(`/transcriptions/${id}/links`);
      // ردٌّ متأخّر لدرس غادرناه كان يُعرض تحت عنوان الدرس الحاضر
      setSelected(cur => { if (cur?.id === id) setLinks(res.items || []); return cur; });
    } catch { /* الفشل يُبقي المعروض — تفريغ اللوحة يوحي بأن لا إحالات */ }
  }, [api]);

  useEffect(() => { loadLinks(selected?.id); }, [selected?.id, loadLinks]);

  const suggestLinks = async () => {
    setLinksBusy(true);
    try {
      const before = links.filter(l => l.status === 'suggested').length;
      const res = await api(`/transcriptions/${selected.id}/links/suggest`, { method: 'POST' });
      setLinks(res.items || []);
      const added = (res.counts?.suggested || 0) - before;
      toast.success(added > 0 ? `اقتُرحت ${added} إحالة` : 'لا إحالات جديدة وجدت لها مواضع');
    } catch (err) { toast.error(err.message); }
    finally { setLinksBusy(false); }
  };

  const decideLink = async (link, status) => {
    try {
      const res = await api(`/transcriptions/${selected.id}/links/${link.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      setLinks(ls => ls.map(l => (l.id === link.id ? res.item : l)));
      toast.success(res.message);
    } catch (err) { toast.error(err.message); }
  };

  const loadBenefits = useCallback(async (id) => {
    if (!id) { setBenefits([]); setBenefitCounts({ suggested: 0, approved: 0, published: 0 }); return; }
    try {
      const res = await api(`/transcriptions/${id}/benefits`);
      setSelected(cur => {
        if (cur?.id !== id) return cur;
        setBenefits(res.items || []);
        setBenefitCounts(res.counts || { suggested: 0, approved: 0, published: 0 });
        return cur;
      });
    } catch { /* انقطاعٌ لحظي كان يُفرغ اللوحة فيُعاد استخراجٌ كلفته عشرات النداءات */ }
  }, [api]);

  useEffect(() => { loadBenefits(selected?.id); }, [selected?.id, loadBenefits]);

  // الاستخراج خارج الطلب كبقيّة التحليل — نتابع analysis_state ثم نعيد الجلب
  const extractBenefits = async () => {
    setBenefitsBusy(true);
    try {
      await watchAnalysis(
        id => api(`/transcriptions/${id}/benefits/extract`, { method: 'POST' }),
        'اكتمل استخراج الفوائد',
        loadBenefits
      );
    } catch (err) { toast.error(err.message); }
    finally { setBenefitsBusy(false); }
  };

  const patchBenefit = async (benefit, body, done) => {
    try {
      const res = await api(`/transcriptions/${selected.id}/benefits/${benefit.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setBenefits(list => list.map(b => (b.id === benefit.id ? res.benefit : b)));
      await loadBenefits(selected.id);
      if (done) toast.success(done);
    } catch (err) { toast.error(err.message); }
  };

  const deleteBenefit = async (benefit) => {
    if (!window.confirm(`حذف الفائدة «${benefit.title}»؟`)) return;
    try {
      await api(`/transcriptions/${selected.id}/benefits/${benefit.id}`, { method: 'DELETE' });
      setBenefits(list => list.filter(b => b.id !== benefit.id));
      await loadBenefits(selected.id);
    } catch (err) { toast.error(err.message); }
  };

  // الاقتطاع بـPHP خالص على الخادم — وما لا تُقصّ حاويته يبقى له الرابط
  const downloadClip = async (benefit) => {
    try {
      const res = await fetch(`${API}/transcriptions/${selected.id}/benefits/${benefit.id}/clip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => null);
        throw new Error(msg?.error || 'تعذّر اقتطاع المقطع');
      }
      const objUrl = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${benefit.title}.${(selected.audio_ext === 'mp3' ? 'mp3' : selected.audio_ext) || 'mp3'}`;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) { toast.error(err.message); }
  };

  const exportBenefits = async (format) => {
    try {
      const res = await fetch(`${API}/transcriptions/${selected.id}/benefits/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => null);
        throw new Error(msg?.error || 'فشل التصدير');
      }
      const objUrl = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${selected.title || 'benefits'}.${format}`;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) { toast.error(err.message); }
  };

  const approveReference = async (ref) => {
    setRefReviewBusy(ref.id);
    try {
      const res = await api(`/transcriptions/${selected.id}/references/${ref.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'verified' }),
      });
      setSelected(s => (s?.id !== selected.id ? s : {
        ...s,
        references_json: (s.references_json || []).map(r => (r.id === ref.id ? res.reference : r)),
      }));
      toast.success('اعتُمد المرجع');
    } catch (err) { toast.error(err.message); }
    finally { setRefReviewBusy(null); }
  };

  const copyChaptersForYoutube = () => {
    if (chapters.length === 0) return;
    const lines = chapters.map(c => `${fmtChapterTime(c.start_seconds)} ${c.title}`);
    if (selected.youtube_url) lines.unshift('🔗 ' + selected.youtube_url);
    const text = lines.join('\n');
    navigator.clipboard.writeText(text).then(
      () => toast.success('تم النسخ — الصق في وصف فيديو يوتيوب'),
      () => toast.error('فشل النسخ'),
    );
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>مُفرّغ — التفريغ النصي</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
        {/* Sidebar */}
        <div>
          {/* Upload Card */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>تفريغ جديد</div>

            <div style={{ display: 'flex', background: 'var(--surface-alt)', borderRadius: 8, padding: 3, marginBottom: 12, gap: 3 }}>
              {[
                { key: 'file', label: '🎙️ ملف' },
                { key: 'platforms', label: '🎬 منصّات' },
                { key: 'youtube', label: '▶️ يوتيوب' },
                { key: 'url', label: '🔗 رابط' },
              ].map(opt => (
                <button key={opt.key} onClick={() => setInputMode(opt.key)} style={{
                  flex: 1, padding: '6px 8px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: inputMode === opt.key ? '#fff' : 'transparent',
                  color: inputMode === opt.key ? 'var(--text-strong)' : 'var(--text-muted)',
                  boxShadow: inputMode === opt.key ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                  transition: 'all .15s'
                }}>{opt.label}</button>
              ))}
            </div>

            <div className="form-group">
              <input className="form-input" placeholder="عنوان الدرس (اختياري)" value={title} onChange={e => setTitle(e.target.value)} />
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <select className="form-select" value={pickClient} onChange={e => setPickClient(e.target.value)}>
                <option value="">— الشيخ/العميل —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select className="form-select" value={pickProject} onChange={e => setPickProject(e.target.value)}>
                <option value="">— المشروع —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>

            {inputMode === 'file' && (
              <>
                <label className="btn btn-primary" style={{ display: 'block', textAlign: 'center', cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? .7 : 1 }}>
                  {processing
                    ? (batchProgress ? `⏳ ${batchProgress.done}/${batchProgress.total}` : '⏳ جاري التفريغ...')
                    : '🎙️ اختر ملفاً واحداً أو أكثر'}
                  <input type="file" accept="audio/*,video/*" multiple onChange={handleUpload} style={{ display: 'none' }} disabled={processing} />
                </label>
                {uploadPct !== null && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, color: 'var(--text-muted)' }}>
                      <span>{uploadPct < 100 ? '📤 جاري رفع الملف...' : '✅ تم الرفع — يبدأ التفريغ الآن'}</span>
                      <span style={{ fontWeight: 600 }}>{uploadPct}%</span>
                    </div>
                    <div style={{ height: 6, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${uploadPct}%`, height: '100%', background: 'var(--primary)', transition: 'width .2s' }} />
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-faint)', textAlign: 'center', marginTop: 6, lineHeight: 1.9 }}>
                  يمكن اختيار عدّة ملفات معاً (Batch upload)
                  <br />
                  الملفات الكبيرة تُقسَّم تلقائياً، وملفات الفيديو يُستخرج صوتها وحده قبل التفريغ.
                </div>
              </>
            )}

            {inputMode === 'platforms' && (
              <div>
                <input
                  className="form-input"
                  placeholder="https://youtube.com/... أو tiktok.com/... أو instagram.com/..."
                  value={mediaUrl}
                  onChange={e => setMediaUrl(e.target.value)}
                  style={{ marginBottom: 8 }}
                  disabled={mediaLoading || processing}
                  dir="ltr"
                />
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', opacity: (mediaLoading || processing || !mediaUrl.trim()) ? .7 : 1 }}
                  onClick={handleMediaUrl}
                  disabled={mediaLoading || processing || !mediaUrl.trim()}
                >
                  {mediaLoading ? '⏳ جاري التحميل عبر Cobalt...' : processing ? '⏳ جاري التفريغ...' : '🎬 تحميل وتفريغ'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7, padding: 8, background: 'var(--gray-50, var(--surface-alt))', borderRadius: 6, border: '1px solid var(--gray-200)' }}>
                  يدعم: YouTube · TikTok · Instagram · X (Twitter) · Facebook · Reddit · SoundCloud · Twitch...
                  <br/>
                  ⚙️ يتطلّب ضبط <strong>Cobalt API URL</strong> و <strong>Api-Key</strong> من <a href="/settings" style={{ color: 'var(--primary)' }}>الإعدادات</a>.
                  الرسمي <code>api.cobalt.tools</code> محجوب من الخوادم — استخدم instance من{' '}
                  <a href="https://instances.cobalt.best" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>instances.cobalt.best</a>.
                </div>
              </div>
            )}

            {inputMode === 'youtube' && (
              <div>
                <input
                  className="form-input"
                  placeholder="https://youtu.be/..."
                  value={youtubeUrl}
                  onChange={e => setYoutubeUrl(e.target.value)}
                  style={{ marginBottom: 8 }}
                  disabled={ytLoading || processing}
                  dir="ltr"
                />
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', opacity: (ytLoading || processing || !youtubeUrl.trim()) ? .7 : 1 }}
                  onClick={handleYoutube}
                  disabled={ytLoading || processing || !youtubeUrl.trim()}
                >
                  {ytLoading ? '⏳ جاري التحميل من يوتيوب...' : processing ? '⏳ جاري التفريغ...' : '▶️ تفريغ من يوتيوب'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7, padding: 8, background: 'var(--gray-50, var(--surface-alt))', borderRadius: 6, border: '1px solid var(--gray-200)' }}>
                  يجرّب الخادم بالترتيب: <strong>ترجمة الفيديو اليدوية</strong> (الأدقّ والأسرع) ← <strong>صوت الفيديو</strong> ←
                  <strong> Cobalt</strong> ← <strong>الترجمة الآلية</strong>.
                  <br/>
                  ⚠️ يوتيوب يحجب عناوين الخوادم، فإن فشلت كل المسارات فاضبط <strong>وكيلاً (Proxy)</strong> أو{' '}
                  <strong>Cobalt Api-Key</strong> من <a href="/settings" style={{ color: 'var(--primary)' }}>الإعدادات</a>،
                  أو استخدم تبويب "🔗 رابط". سبب الفشل الدقيق يظهر على بطاقة التفريغ.
                </div>
              </div>
            )}

            {inputMode === 'url' && (
              <div>
                <input
                  className="form-input"
                  placeholder="https://...mp3 أو رابط مباشر"
                  value={directUrl}
                  onChange={e => setDirectUrl(e.target.value)}
                  style={{ marginBottom: 8 }}
                  disabled={directLoading || processing}
                  dir="ltr"
                />
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', opacity: (directLoading || processing || !directUrl.trim()) ? .7 : 1 }}
                  onClick={handleDirectUrl}
                  disabled={directLoading || processing || !directUrl.trim()}
                >
                  {directLoading ? '⏳ جاري التحميل...' : processing ? '⏳ جاري التفريغ...' : '🔗 تحميل وتفريغ'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7, padding: 8, background: 'var(--gray-50, var(--surface-alt))', borderRadius: 6, border: '1px solid var(--gray-200)' }}>
                  <strong>كيف تستخدم هذا التبويب لفيديو يوتيوب؟</strong><br/>
                  1. افتح <a href="https://cobalt.tools" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>cobalt.tools</a> في متصفّحك<br/>
                  2. الصق رابط يوتيوب، اختر "audio" → mp3<br/>
                  3. سيظهر زرّ "Download" — انقره بالزرّ الأيمن واختر "Copy link"<br/>
                  4. الصق الرابط هنا واضغط "تحميل وتفريغ"<br/>
                  <br/>
                  هذا يعمل لأن PoToken الخاص بـ YouTube يُولَّد في متصفّحك (لا في خادمنا).
                </div>
              </div>
            )}
          </div>

          {/* Search + Filter */}
          <div className="card" style={{ marginBottom: 12, padding: 12 }}>
            <input
              className="form-input"
              placeholder="🔍 ابحث في التفريغات..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              style={{ marginBottom: 8 }}
            />
            <select className="form-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
              <option value="">كل المشايخ</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* List */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              التفريغات ({transcriptions.length})
            </div>
            {loading ? <div className="loader">جاري التحميل...</div> : transcriptions.length === 0 ? (
              <div className="empty" style={{ padding: 20 }}>لا توجد نتائج</div>
            ) : (
              transcriptions.map(tr => (
                <div key={tr.id} onClick={() => selectTr(tr)}
                  style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selected?.id === tr.id ? 'var(--c-blue-100)' : undefined, borderRadius: 4, position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: 500, fontSize: 14, flex: 1 }}>{tr.title}</div>
                    <button
                      onClick={(e) => deleteTr(tr.id, e)}
                      title="حذف"
                      style={{ background: 'none', border: 'none', color: 'var(--c-coral)', cursor: 'pointer', fontSize: 14, padding: 2 }}
                    >🗑️</button>
                  </div>
                  {tr.client?.name && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>👤 {tr.client.name}{tr.project?.title && ` · 📂 ${tr.project.title}`}</div>
                  )}
                  {tr.chunks_total > 1 && tr.chunks_done < tr.chunks_total && (
                    <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 2 }}>
                      🧩 {tr.chunks_done}/{tr.chunks_total} أجزاء
                      {tr.next_attempt_at && ` · التالي: ${new Date(tr.next_attempt_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}`}
                    </div>
                  )}
                  {tr.stage && tr.wait_reason && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>⏱ {tr.wait_reason}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span className={`badge badge-${tr.status === 'completed' ? 'completed' : tr.status === 'processing' ? 'in_progress' : tr.status === 'failed' ? 'revision' : 'pending'}`}>
                      {tr.status === 'completed' ? 'مكتمل' : tr.status === 'processing' ? (tr.chunks_total > 1 ? `جزء ${tr.chunks_done}/${tr.chunks_total}` : `جاري ${tr.progress ? tr.progress + '%' : ''}`) : tr.status === 'failed' ? 'فشل' : 'جديد'}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{fmtDateBoth(tr.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Editor */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!selected ? (
            <div className="card">
              <div className="empty"><div className="empty-icon">🎙️</div>اختر تفريغاً أو ارفع ملفاً صوتياً جديداً</div>
            </div>
          ) : (
            <>
              {/* Audio/Video Player — native controls so user can always play, seek, scrub */}
              {selected.audio_url && (
                <div className="card" style={{ padding: '14px 18px' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {isVideo ? '🎬' : '🎙️'} {selected.title}
                  </div>

                  {isVideo ? (
                    <video
                      ref={audioRef}
                      src={selected.audio_url}
                      controls
                      playsInline
                      preload="metadata"
                      onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                      onLoadedMetadata={e => { setDuration(e.target.duration); e.target.playbackRate = playbackSpeed; applyPendingSeek(selected.id); }}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onError={() => toast.error('تعذّر تحميل الملف الصوتي/المرئي')}
                      style={{ width: '100%', maxHeight: 360, background: '#000', borderRadius: 8 }}
                    />
                  ) : (
                    <audio
                      ref={audioRef}
                      src={selected.audio_url}
                      controls
                      preload="metadata"
                      onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                      onLoadedMetadata={e => { setDuration(e.target.duration); e.target.playbackRate = playbackSpeed; applyPendingSeek(selected.id); }}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onError={() => toast.error('تعذّر تحميل الملف الصوتي')}
                      style={{ width: '100%' }}
                    />
                  )}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>سرعة التشغيل:</span>
                    {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(s => (
                      <button
                        key={s}
                        type="button"
                        className={`btn btn-sm ${playbackSpeed === s ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => {
                          setPlaybackSpeed(s);
                          if (audioRef.current) audioRef.current.playbackRate = s;
                        }}
                        style={{ padding: '2px 8px', fontSize: 12 }}
                      >{s}x</button>
                    ))}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                    لو لم يفتح المشغّل: <a href={selected.audio_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>افتح الملف مباشرة</a>
                  </div>
                </div>
              )}

              {/* Meta editor: client/project */}
              <div className="card" style={{ padding: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <select className="form-select" value={selected.client_id || ''} onChange={e => updateMeta({ client_id: e.target.value || null })}>
                    <option value="">— الشيخ/العميل —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select className="form-select" value={selected.project_id || ''} onChange={e => updateMeta({ project_id: e.target.value || null })}>
                    <option value="">— المشروع —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              </div>

              {/* Chunked progress (only when transcription is split across hours) */}
              {selected.chunks_total > 1 && selected.chunks_done < selected.chunks_total && (
                <div className="card" style={{ padding: 14, background: 'var(--info-light, var(--c-blue-100))', borderColor: 'var(--info, var(--c-blue-500))' }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    🧩 ملف طويل — تفريغ على مراحل
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${(selected.chunks_done / selected.chunks_total) * 100}%`, height: '100%', background: 'var(--primary)', transition: 'width .3s' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.chunks_done}/{selected.chunks_total}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    كل جزء يُفرَّغ حين تتّسع له حصّة Groq في الساعة، بترتيب الطابور.
                    {selected.next_attempt_at && (
                      <span> المحاولة التالية تلقائياً عند: <strong>{new Date(selected.next_attempt_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</strong></span>
                    )}
                  </div>
                  {(user?.permissions || []).includes('transcription.manage') && (
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={processing}
                      onClick={async () => {
                        setProcessing(true);
                        try {
                          await api(`/transcriptions/${selected.id}/process-next-chunk`, { method: 'POST' });
                          toast.success('رُفع الانتظار — يتقدّم في دور الطابور التالي');
                          selectTr({ id: selected.id });
                        } catch (err) { toast.error(err.message); }
                        setProcessing(false);
                      }}
                    >
                      ⚡ رفع الانتظار وإعادة الأجزاء الفاشلة
                    </button>
                  )}
                </div>
              )}

              {/* Transcription progress (non-chunked single-shot jobs) */}
              {selected.status === 'processing' && !(selected.chunks_total > 1) && (() => {
                const pct = Math.max(5, selected.progress || 0);
                const stage = pct >= 82 ? 'finalize' : 'transcribe';
                const Step = ({ icon, label, state }) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                    color: state === 'pending' ? 'var(--text-muted)' : 'inherit',
                    fontWeight: state === 'active' ? 600 : 400 }}>
                    <span>{state === 'done' ? '✅' : state === 'active' ? icon : '⚪'}</span>
                    <span>{label}</span>
                    {state === 'active' && <span style={{ marginInlineStart: 'auto', fontSize: 12 }}>… جارٍ</span>}
                  </div>
                );
                return (
                  <div className="card" style={{ padding: 14 }}>
                    <div style={{ fontWeight: 600, marginBottom: 10 }}>⏳ سير التفريغ — {pct}%</div>
                    <div style={{ height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary)', transition: 'width .4s' }} />
                    </div>
                    {selected.wait_reason && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>⏱ {selected.wait_reason}</div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <Step icon="📤" label="تم رفع الملف" state="done" />
                      <Step icon="🎙️" label="تفريغ الصوت إلى نص" state={stage === 'transcribe' ? 'active' : 'done'} />
                      <Step icon="🧹" label="التنسيق والتلخيص واستخراج الفصول" state={stage === 'finalize' ? 'active' : 'pending'} />
                      <Step icon="🏁" label="اكتمال التفريغ" state="pending" />
                    </div>
                  </div>
                );
              })()}

              {selected.status === 'failed' && selected.error && (
                <div className="card" style={{ borderRight: '4px solid var(--c-coral)', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--c-coral)' }}>سبب فشل التفريغ</div>
                  <div style={{ fontSize: 14, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{selected.error}</div>
                </div>
              )}

              {/* Text Panel */}
              <div className="card">
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`badge badge-${selected.status === 'completed' ? 'completed' : selected.status === 'processing' ? 'in_progress' : 'revision'}`}>
                      {selected.status === 'completed' ? 'مكتمل' : selected.status === 'processing' ? (selected.chunks_total > 1 ? `جزء ${selected.chunks_done}/${selected.chunks_total}` : `جاري التفريغ ${selected.progress ? selected.progress + '%' : ''}`) : 'فشل'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('txt')}>📄 TXT</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('docx')} title="بهوية موقع الشيخ">📝 Word</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('pdf')} title="بهوية موقع الشيخ">📕 PDF</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('site')} title="صفحة الموقع بهوية الشيخ">🌐 صفحة</button>
                    <button className="btn btn-sm btn-outline" onClick={() => exportFile('srt')}>🎬 SRT</button>
                    <button className="btn btn-sm btn-outline" onClick={() => retranscribe(selected.id)} disabled={processing}>
                      {processing ? '⏳' : '🔄'} إعادة
                    </button>
                    {editingText ? (
                      <button className="btn btn-sm btn-primary" onClick={async () => { await saveText(); setEditingText(false); }}>💾 حفظ</button>
                    ) : (
                      <>
                        <button className="btn btn-sm btn-outline" onClick={() => setEditingText(true)} disabled={selected.status !== 'completed'}>✏️ تعديل</button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => { setShowDiff(d => !d); setShowConfidence(false); }}
                          disabled={selected.status !== 'completed' || !selected.raw_text}
                          title="ما أضافه التنسيق الآلي أو حذفه من كلام الشيخ"
                        >
                          {showDiff ? '📄 النصّ' : '🔍 الفرق عن الخام'}
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => { setShowConfidence(c => !c); setShowDiff(false); }}
                          disabled={segments.length === 0}
                          title={segments.length === 0
                            ? 'لا مقاطع محفوظة لهذا التفريغ — أعد التفريغ لتُحفظ مؤشّرات الثقة'
                            : 'مواضع ضعف ثقة المزوّد — استمع إليها وحدها'}
                        >
                          {showConfidence ? '📄 النصّ' : '🌡️ خريطة الثقة'}
                        </button>
                      </>
                    )}
                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }} onClick={() => deleteTr(selected.id)}>
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Unified text view: same content (formatted_text) shown either as
                    clickable+karaoke spans, or as a textarea when editing. */}
                {showConfidence && !editingText ? (
                  <>
                    <div style={{
                      display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
                      padding: '8px 10px', marginBottom: 10, borderRadius: 6,
                      background: 'var(--surface-alt)', fontSize: 12,
                    }}>
                      <span style={{ fontWeight: 600 }}>ثقة المزوّد في كل مقطع</span>
                      {confidence.has_confidence ? (
                        <>
                          <span><span style={{ background: CONF_TONE.weak.bg, padding: '1px 5px', borderRadius: 3 }}>ضعيفة</span> {confidence.weak} مقطعاً ({fmtChapterTime(confidence.weak_seconds || 0)})</span>
                          <span><span style={{ background: CONF_TONE.fair.bg, padding: '1px 5px', borderRadius: 3 }}>متوسّطة</span> {confidence.fair}</span>
                          <span style={{ color: 'var(--text-muted)' }}>من {confidence.total} مقطعاً</span>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>
                          هذا المزوّد لم يُعد مؤشّر ثقة — المقاطع معروضة بلا حكم.
                        </span>
                      )}
                    </div>

                    {weakSegments.length > 0 && (
                      <div style={{ marginBottom: 12, fontSize: 13 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>مواضع تحتاج سماعاً ({weakSegments.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                          {weakSegments.map((seg, i) => (
                            <button
                              key={i}
                              className="btn btn-sm btn-outline"
                              style={{ justifyContent: 'flex-start', textAlign: 'right', fontWeight: 400 }}
                              onClick={() => seekToWord(seg.start)}
                            >
                              <span style={{ fontFamily: 'monospace', direction: 'ltr', marginInlineEnd: 8 }}>{fmtChapterTime(seg.start)}</span>
                              {seg.text.slice(0, 80)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div
                      dir="rtl"
                      style={{
                        minHeight: 300, maxHeight: 600, overflowY: 'auto',
                        lineHeight: 2.2, fontSize: 15, padding: '4px 6px',
                      }}
                    >
                      {segments.map((seg, i) => (
                        <span
                          key={i}
                          onClick={() => seekToWord(seg.start)}
                          title={`${fmtChapterTime(seg.start)} — ثقة ${CONF_TONE[seg.level]?.label || 'مجهولة'}${seg.confidence != null ? ` (${Math.round(seg.confidence * 100)}%)` : ''}`}
                          style={{
                            background: CONF_TONE[seg.level]?.bg || 'transparent',
                            borderRadius: 3, padding: '1px 2px', cursor: 'pointer',
                          }}
                        >{seg.text}{' '}</span>
                      ))}
                    </div>

                    <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                      💡 الأحمر ثقة ضعيفة والأصفر متوسّطة — اضغط أي مقطع للانتقال إليه في الصوت
                    </div>
                  </>
                ) : showDiff && !editingText ? (
                  <>
                    <div style={{
                      display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
                      padding: '8px 10px', marginBottom: 10, borderRadius: 6,
                      background: 'var(--surface-alt)', fontSize: 12,
                    }}>
                      <span style={{ fontWeight: 600 }}>مقارنة النصّ الخام بالمنسّق</span>
                      <span><span style={{ background: 'var(--c-success-soft)', color: 'var(--c-success)', padding: '1px 5px', borderRadius: 3 }}>أُضيف</span> {diffCounts.added} كلمة</span>
                      <span><span style={{ background: 'var(--c-coral-soft)', color: 'var(--c-coral)', padding: '1px 5px', borderRadius: 3, textDecoration: 'line-through' }}>حُذف</span> {diffCounts.removed} كلمة</span>
                      <span style={{ color: 'var(--text-muted)' }}>التشكيل والترقيم والفقرات لا تُحسب تغييراً</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button className="btn btn-sm btn-outline" onClick={regenFormat} disabled={formatBusy || selected.analysis_state === 'running'}>
                        {formatBusy ? 'جارِ التنسيق...' : '♻️ إعادة التنسيق بالمدقّق'}
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={revertFormat} disabled={formatBusy}>↩︎ استعادة النصّ السابق</button>
                    </div>
                    {formatCheck && (
                      <div data-testid="format-check" style={{
                        padding: '8px 10px', marginBottom: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.9,
                        background: formatCheck.match ? 'var(--c-success-soft)' : 'var(--c-coral-soft)',
                      }}>
                        <div style={{ fontWeight: 600 }}>
                          {formatCheck.match
                            ? `✓ المدقّق: تسلسل الكلمات مطابق للخام ١٠٠٪ (${formatCheck.raw_words} كلمة)`
                            : `✗ المدقّق: النصّ المنسّق يخالف الخام (${formatCheck.raw_words} كلمة خاماً، ${formatCheck.formatted_words} منسّقاً)`}
                        </div>
                        {(formatCheck.diffs || []).slice(0, 5).map((d, i) => (
                          <div key={i}>«{d.raw || '—'}» ← «{d.formatted || '—'}»</div>
                        ))}
                        {formatCheck.report && (
                          <div>
                            آخر تنسيق: {formatCheck.report.chunks_total} قطعة — نُسّقت {formatCheck.report.formatted}،
                            رُفضت محاولات {formatCheck.report.rejected}، أُعيدت {formatCheck.report.retries}،
                            بقيت خاماً {(formatCheck.report.raw_kept || []).length}
                            {(formatCheck.report.projected || []).length > 0 && `، أُسقط ترقيمها على لفظ الخام ${formatCheck.report.projected.length}`}
                          </div>
                        )}
                        {(formatCheck.report?.projected || []).map(k => (
                          <div key={`p${k.index}`}>
                            ترقيم مُسقَط: القطعة {k.index + 1} — طابق {Math.round(k.coverage * 100)}٪ من الكلمات
                            {k.start_sec != null && (
                              <button className="btn btn-link btn-sm" onClick={() => seekToWord(k.start_sec)}>
                                ≈{formatTime(k.start_sec)}–{formatTime(k.end_sec)}
                              </button>
                            )}
                          </div>
                        ))}
                        {(formatCheck.report?.raw_kept || []).map(k => (
                          <div key={k.index}>
                            {k.label}: القطعة {k.index + 1}
                            {k.start_sec != null && (
                              <button className="btn btn-link btn-sm" onClick={() => seekToWord(k.start_sec)}>
                                ≈{formatTime(k.start_sec)}–{formatTime(k.end_sec)}
                              </button>
                            )}
                            {k.reason ? ` — ${k.reason}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {corrections.length > 0 && (
                      <details style={{ marginBottom: 10, fontSize: 12 }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>سجلّ التصحيحات ({corrections.length})</summary>
                        {corrections.map(c => (
                          <div key={c.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--border)', opacity: c.reverted_at ? 0.55 : 1 }}>
                            «{c.expected_text}» ← «{c.new_text}» — {c.reason}
                            {' '}<span style={{ color: 'var(--text-muted)' }}>({c.source === 'claude' ? 'Claude' : (c.applier?.name || '—')}{c.kind === 'asr' ? '، خطأ سماع' : '، تنسيق'})</span>
                            {c.reverted_at
                              ? <span style={{ marginInlineStart: 6 }}>عُكس</span>
                              : <button className="btn btn-link btn-sm" onClick={() => revertCorrection(c)}>عكس</button>}
                          </div>
                        ))}
                      </details>
                    )}
                    <div
                      dir="rtl"
                      style={{
                        minHeight: 300, maxHeight: 600, overflowY: 'auto',
                        lineHeight: 2.2, fontSize: 15, padding: '4px 6px', whiteSpace: 'pre-wrap',
                      }}
                    >
                      {diffCounts.added === 0 && diffCounts.removed === 0 ? (
                        <div style={{ color: 'var(--c-success)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                          ✓ التنسيق لم يضف كلمة ولم يحذف كلمة — التغيير كلّه تشكيل وترقيم وفقرات.
                        </div>
                      ) : diffParts.map((part, i) => (
                        <span
                          key={i}
                          style={
                            part.type === 'add'
                              ? { background: 'var(--c-success-soft)', color: 'var(--c-success)', borderRadius: 3, padding: '1px 3px', fontWeight: 600 }
                              : part.type === 'del'
                                ? { background: 'var(--c-coral-soft)', color: 'var(--c-coral)', borderRadius: 3, padding: '1px 3px', textDecoration: 'line-through' }
                                : undefined
                          }
                        >{part.text}{' '}</span>
                      ))}
                    </div>
                    <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                      💡 الأخضر ما أضافه التنسيق الآلي، والأحمر ما حذفه — راجعهما قبل أن يُنشر النصّ باسم الشيخ
                    </div>
                  </>
                ) : editingText ? (
                  <>
                    <textarea
                      dir="rtl"
                      className="form-textarea"
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      style={{ width: '100%', minHeight: 360, lineHeight: 2, fontSize: 15, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>عدّل النصّ ثم اضغط "حفظ"</span>
                      <span>{editText.split(/\s+/).filter(Boolean).length} كلمة</span>
                    </div>
                  </>
                ) : (
                  <div
                    dir="rtl"
                    style={{
                      minHeight: 300, lineHeight: 2.4, fontSize: 15,
                      overflowY: 'auto', maxHeight: 600,
                      padding: '4px 6px', wordSpacing: 2,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {alignedTokens.length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                        {selected.status === 'completed' ? 'لا يوجد نص' : `جارٍ التفريغ… ${selected.progress ? selected.progress + '%' : ''}`}
                      </div>
                    ) : alignedTokens.map((tok, i) => {
                      if (tok.isWhitespace) return tok.text;
                      const isActive = tok.start !== null && currentTime >= tok.start && currentTime < (tok.end ?? tok.start + 0.5);
                      return (
                        <span
                          key={i}
                          ref={isActive ? activeWordRef : null}
                          onClick={() => tok.start !== null && seekToWord(tok.start)}
                          title={tok.start !== null ? formatTime(tok.start) : ''}
                          style={{
                            cursor: tok.start !== null ? 'pointer' : 'default',
                            background: isActive ? 'var(--c-gold)' : 'transparent',
                            borderRadius: 3,
                            padding: '1px 2px',
                            transition: 'background 0.15s',
                            fontWeight: isActive ? 600 : 400,
                          }}
                        >{tok.text}</span>
                      );
                    })}
                  </div>
                )}

                {!editingText && !showDiff && !showConfidence && words && (
                  <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                    💡 اضغط على أي كلمة للانتقال إليها في الصوت — تتظلّل الكلمة الحاليّة أثناء التشغيل
                  </div>
                )}
                {!editingText && !showDiff && !showConfidence && !words && selected.status === 'completed' && (
                  <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                    ⚠️ هذا التفريغ بلا طوابع زمنية لكل كلمة — اضغط 🔄 لإعادة التفريغ مع timestamps
                  </div>
                )}
              </div>

              {/* حالة التحليل — الحالة تصير «مكتمل» قبل أن يبدأ، فبدون هذا
                  الشريط لا يعرف المحرّر أالملخّص قيد التوليد أم فشل */}
              {selected.status === 'completed' && (
                <details style={{ margin: '12px 0' }} data-tour="transcription-review" onToggle={e => setReviewOpen(e.currentTarget.open)}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>🧭 مواضع المراجعة (اختلاف المحرّكين، الثقة، الآيات، الأحاديث، التنسيق)</summary>
                  {reviewOpen && (
                    <div style={{ marginTop: 10 }}>
                      <ReviewItems transcriptionId={selected.id} />
                    </div>
                  )}
                </details>
              )}
              {selected.status === 'completed' && selected.analysis_state && (
                <div className="card" style={{
                  background: selected.analysis_state === 'failed' ? 'var(--c-coral-soft)' : 'var(--c-blue-100)',
                  fontSize: 13,
                }}>
                  {selected.analysis_state === 'failed'
                    ? `⚠️ ${selected.analysis_error || 'فشل التحليل'} — أعد التوليد أو راجع مفتاح المزوّد.`
                    : '⏳ التحليل جارٍ — يغطّي النصّ كلّه فيأخذ وقتاً على الدروس الطويلة.'}
                </div>
              )}

              {/* Summary Panel */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>📝 ملخّص التفريغ</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {summaryEdit ? (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={saveSummary} disabled={summaryBusy}>💾 حفظ</button>
                          <button className="btn btn-sm btn-outline" onClick={() => { setSummaryDraft(selected.summary || ''); setSummaryEdit(false); }}>إلغاء</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-sm btn-outline" onClick={() => setSummaryEdit(true)} disabled={!selected.summary}>✏️ تعديل</button>
                          <button className="btn btn-sm btn-outline" onClick={regenSummary} disabled={summaryBusy}>
                            {summaryBusy ? '⏳ جارٍ' : '🔄 توليد'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {summaryEdit ? (
                    <textarea
                      dir="rtl"
                      className="form-textarea"
                      value={summaryDraft}
                      onChange={e => setSummaryDraft(e.target.value)}
                      style={{ width: '100%', minHeight: 140, lineHeight: 1.9, fontSize: 14 }}
                    />
                  ) : selected.summary ? (
                    <div dir="rtl" style={{ fontSize: 14, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
                      {selected.summary}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لا يوجد ملخص بعد — اضغط "🔄 توليد" لإنشائه
                    </div>
                  )}
                </div>
              )}

              {/* Chapters / YouTube Index Panel */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>📑 فهرسة يوتيوب</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-outline" onClick={copyChaptersForYoutube} disabled={chapters.length === 0}>
                        📋 نسخ بصيغة YouTube
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={regenChapters} disabled={chaptersBusy}>
                        {chaptersBusy ? '⏳ جارٍ' : '🔄 توليد'}
                      </button>
                    </div>
                  </div>
                  {chapters.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لا توجد فصول بعد — اضغط "🔄 توليد" لإنشاء عناوين رئيسية مع timestamps
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {chapters.map((ch, i) => (
                        <div
                          key={i}
                          onClick={() => seekToWord(ch.start_seconds)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                            background: 'var(--gray-50, var(--surface-alt))',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100, var(--surface-alt))'}
                          onMouseLeave={e => e.currentTarget.style.background = 'var(--gray-50, var(--surface-alt))'}
                        >
                          <span style={{
                            fontFamily: 'monospace', fontSize: 12, color: 'var(--c-blue-500)',
                            minWidth: 60, textAlign: 'center',
                            background: 'var(--c-blue-100)', padding: '2px 6px', borderRadius: 4,
                          }}>
                            {fmtChapterTime(ch.start_seconds)}
                          </span>
                          <span style={{ fontSize: 14, flex: 1 }}>{ch.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                    💡 اضغط على فصل للانتقال إليه في الصوت — استخدم زر "نسخ" للصق في وصف فيديو يوتيوب
                  </div>
                </div>
              )}

              {/* References Panel */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>
                      📚 الآيات والأحاديث المُكتشَفة
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' }}> ({refs.length})</span>
                    </span>
                    <button className="btn btn-sm btn-outline" onClick={regenReferences} disabled={refsBusy}>
                      {refsBusy ? '⏳ جارٍ' : '🔄 توليد'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {refs.map((r, i) => {
                      const tone = REF_TONE[r.status] || REF_TONE.review;
                      return (
                        <div key={r.id || i} style={{
                          padding: '10px 12px',
                          background: tone.bg,
                          borderRight: `4px solid ${tone.line}`,
                          borderRadius: 6,
                          fontSize: 14, lineHeight: 1.8,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: tone.ink }}>
                              {r.type === 'quran' ? '﴿ آية قرآنية ﴾' : '« حديث نبوي »'}
                            </span>
                            <span className={`badge badge-${tone.badge}`} style={{ fontSize: 10 }}>
                              {r.matched ? 'مطابق للمصحف' : (REF_STATUS_AR[r.status] || 'يحتاج مراجعة')}
                            </span>
                            {r.approved_by && (
                              <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>اعتمده مراجع</span>
                            )}
                          </div>

                          <div className={r.type === 'quran' && r.matched ? 'quran' : undefined}>{r.text}</div>

                          {/* الرسم من المصحف يختلف عمّا التقطه التفريغ — يُعرض الأصل ليقارن المحرّر */}
                          {r.matched && r.spoken && r.spoken !== r.text && (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                              كما وردت في التفريغ: {r.spoken}
                            </div>
                          )}

                          {r.type === 'quran' && r.matched && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                              {[r.surah, r.ayah && `آية ${r.ayah}`].filter(Boolean).join(' · ')} — من المصحف المرجعي
                            </div>
                          )}

                          {r.type === 'quran' && !r.matched && (
                            <div style={{ fontSize: 11, color: 'var(--c-gold-ink)', marginTop: 4 }}>
                              لم تُطابق المصحف المرجعي — راجع اللفظ بنفسك

                            </div>
                          )}

                          {r.type === 'hadith' && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                              {r.source || r.narrator
                                ? [r.narrator, r.source].filter(Boolean).join(' · ')
                                : 'التخريج بشريّ — لا يُخرَّج آلياً'}
                              {!r.approved_by && (r.suggested_source || r.suggested_narrator) && (
                                <span style={{ color: 'var(--text-faint)' }}>
                                  {' '}(اقتراح آلي غير مخرَّج: {[r.suggested_narrator, r.suggested_source].filter(Boolean).join(' · ')})
                                </span>
                              )}
                            </div>
                          )}

                          {canReviewRefs && r.status !== 'verified' && (
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ marginTop: 8 }}
                              disabled={refReviewBusy === r.id}
                              onClick={() => approveReference(r)}
                            >
                              {refReviewBusy === r.id ? '⏳' : '✓ اعتمد'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {refs.length === 0 && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لم تُكتشف مراجع بعد — اضغط "🔄 توليد" لفحص النصّ.
                    </div>
                  )}
                </div>
              )}

              {/* الفوائد — البند ١٥: المحرّر يختار العنوان ويعتمد، والذكاء يقترح */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>
                      💡 الفوائد
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' }}>
                        {' '}({benefitCounts.suggested} مقترحة · {benefitCounts.approved} معتمدة · {benefitCounts.published} منشورة)
                      </span>
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {benefits.length > 0 && (
                        <>
                          <button className="btn btn-sm btn-outline" onClick={() => exportBenefits('csv')} title="جدول يفتحه Excel وبرامج المونتاج">📋 CSV</button>
                          <button className="btn btn-sm btn-outline" onClick={() => exportBenefits('edl')} title="قائمة قطع بدقّة الإطار">🎬 EDL</button>
                          <button className="btn btn-sm btn-outline" onClick={() => exportBenefits('txt')} title="روابط موقوتة تُلصق في وصف الفيديو">🔗 روابط</button>
                        </>
                      )}
                      {canManageBenefits && (
                        <button className="btn btn-sm btn-outline" onClick={extractBenefits} disabled={benefitsBusy}>
                          {benefitsBusy ? '⏳ جارٍ' : '🔄 استخراج'}
                        </button>
                      )}
                    </div>
                  </div>

                  {benefits.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لا فوائد بعد — اضغط "🔄 استخراج" ليقترحها الذكاء، والاعتماد يبقى لك.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {benefits.map(b => {
                        const state = BENEFIT_STATUS[b.status] || BENEFIT_STATUS.suggested;
                        const editing = benefitEditing?.id === b.id;
                        return (
                          <div key={b.id} style={{
                            padding: '10px 12px', borderRadius: 6,
                            background: 'var(--surface-alt)',
                            borderRight: `4px solid var(--c-blue-500)`,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                              <button
                                onClick={() => seekToWord(b.start_seconds)}
                                className="btn btn-sm btn-outline"
                                style={{ fontFamily: 'monospace', direction: 'ltr', padding: '2px 6px' }}
                                title="انتقل إلى موضعها في الصوت"
                              >{b.clock}</button>
                              <span className={`badge badge-${state.badge}`} style={{ fontSize: 10 }}>{state.label}</span>
                              <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{Math.round(b.duration_seconds)}ث</span>
                              {b.topic && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {b.topic}</span>}
                              {b.link && (
                                <a href={b.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>رابط موقوت ↗</a>
                              )}
                            </div>

                            {editing ? (
                              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                <input
                                  className="form-input"
                                  value={benefitEditing.title}
                                  onChange={e => setBenefitEditing(v => ({ ...v, title: e.target.value }))}
                                  style={{ flex: 1 }}
                                />
                                <button className="btn btn-sm btn-primary" onClick={async () => {
                                  await patchBenefit(b, { title: benefitEditing.title }, 'حُفظ العنوان');
                                  setBenefitEditing(null);
                                }}>💾</button>
                                <button className="btn btn-sm btn-outline" onClick={() => setBenefitEditing(null)}>إلغاء</button>
                              </div>
                            ) : (
                              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{b.title}</div>
                            )}

                            {/* ثلاثة عناوين مقترحة ليختار المحرّر — أرخص من إعادة التوليد */}
                            {canManageBenefits && !editing && (b.title_options || []).length > 1 && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                                {b.title_options.filter(t => t !== b.title).map((t, i) => (
                                  <button
                                    key={i}
                                    className="btn btn-sm btn-outline"
                                    style={{ fontWeight: 400, fontSize: 12 }}
                                    onClick={() => patchBenefit(b, { title: t }, 'بُدّل العنوان')}
                                  >{t}</button>
                                ))}
                              </div>
                            )}

                            {/* النصّ حرفيّ من التفريغ — لا يُحرَّر، وهذه ضمانة البند */}
                            <div dir="rtl" style={{ fontSize: 14, lineHeight: 1.9 }}>{b.text}</div>
                            {b.context && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{b.context}</div>
                            )}

                            {canManageBenefits && (
                              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                                {state.next && (
                                  <button className="btn btn-sm btn-outline" onClick={() => patchBenefit(b, { status: state.next }, state.label === 'مقترحة' ? 'اعتُمدت الفائدة' : 'عُلّمت منشورة')}>
                                    {state.nextLabel}
                                  </button>
                                )}
                                {!editing && (
                                  <button className="btn btn-sm btn-outline" onClick={() => setBenefitEditing({ id: b.id, title: b.title })}>✏️ العنوان</button>
                                )}
                                {b.clippable && (
                                  <button className="btn btn-sm btn-outline" onClick={() => downloadClip(b)} title="يُقتطع على الخادم بلا برامج خارجية">
                                    ✂️ المقطع
                                  </button>
                                )}
                                <button className="btn btn-sm btn-outline" style={{ color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }} onClick={() => deleteBenefit(b)}>🗑️</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* إحالات السلسلة — البند ٢٤: «كما مرّ معنا» إلى أي درس؟ */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>
                      🔗 إحالات السلسلة
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' }}> ({links.length})</span>
                    </span>
                    {canReviewRefs && (
                      <button className="btn btn-sm btn-outline" onClick={suggestLinks} disabled={linksBusy}>
                        {linksBusy ? '⏳ جارٍ' : '🔄 ابحث عن الإحالات'}
                      </button>
                    )}
                  </div>

                  {links.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لا إحالات بعد — اضغط "🔄" ليُبحث عن «كما مرّ معنا» وأخواتها في دروس المشروع السابقة
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {links.map(link => (
                        <div key={link.id} style={{
                          padding: 12, borderRadius: 6,
                          background: 'var(--gray-50, var(--surface-alt))',
                          opacity: link.status === 'dismissed' ? 0.55 : 1,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{
                                fontSize: 11, padding: '2px 8px', borderRadius: 10,
                                background: link.status === 'approved' ? 'var(--c-blue-100)' : 'var(--surface)',
                                color: link.status === 'approved' ? 'var(--c-blue-500)' : 'var(--text-muted)',
                              }}>{link.status_label}</span>
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>«{link.phrase}»</span>
                            </span>
                            {link.clock && (
                              <span
                                onClick={() => seekToWord(link.start_seconds)}
                                style={{
                                  fontFamily: 'monospace', fontSize: 11, cursor: 'pointer',
                                  background: 'var(--c-blue-100)', color: 'var(--c-blue-500)',
                                  padding: '2px 6px', borderRadius: 4,
                                }}
                              >{link.clock}</span>
                            )}
                          </div>

                          <div dir="rtl" style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 6 }}>…{link.context}…</div>

                          {link.target && (
                            <div style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ color: 'var(--text-muted)' }}>← يُحال إلى:</span>
                              <a
                                href={`/transcription?id=${link.target.id}${link.target.seconds != null ? `&t=${Math.floor(link.target.seconds)}` : ''}`}
                                onClick={e => {
                                  // إعادة تحميل المستند كانت تمحو تحرير المحرّر بلا تأكيد
                                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
                                  e.preventDefault();
                                  if (link.target.seconds != null) {
                                    pendingSeek.current = { id: link.target.id, at: link.target.seconds };
                                  }
                                  selectTr({ id: link.target.id }).catch(() => toast.error('تعذّر فتح الدرس المُحال إليه'));
                                }}
                              >{link.target.title}</a>
                              {link.target.clock && (
                                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-faint)' }}>{link.target.clock}</span>
                              )}
                            </div>
                          )}

                          {canReviewRefs && link.status === 'suggested' && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                              <button className="btn btn-sm btn-outline" onClick={() => decideLink(link, 'approved')}>✔️ اعتماد</button>
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }}
                                onClick={() => decideLink(link, 'dismissed')}
                              >✖️ رفض</button>
                            </div>
                          )}
                          {link.decided_by && (
                            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>
                              بتّ فيها: {link.decided_by}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                    💡 الإحالة إلى الدرس الخطأ نسبةُ كلامٍ إلى موضع لم يُقل فيه — تُراجَع قبل اعتمادها
                  </div>
                </div>
              )}

              {/* تجهيز النشر — البند ٢٠: هذه بالضبط الحقول التي تُملأ يدوياً عند كل نشر */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>📤 تجهيز النشر</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {publishEdit ? (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={savePublish} disabled={publishBusy}>💾 حفظ</button>
                          <button className="btn btn-sm btn-outline" onClick={() => setPublishEdit(false)}>إلغاء</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-sm btn-outline" onClick={openPublishEdit}>✏️ تعديل</button>
                          <button className="btn btn-sm btn-outline" onClick={regenPublish} disabled={publishBusy}>
                            {publishBusy ? '⏳ جارٍ' : '🔄 توليد'}
                          </button>
                          {canPublish && (
                            <button className="btn btn-sm btn-primary" onClick={openPublish}>
                              {selected.published_at ? '🌐 إعادة النشر' : '🌐 نشر للموقع'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {publishEdit ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[
                        ['publish_title', 'العنوان', 'input'],
                        ['publish_description', 'الوصف للأرشفة', 'textarea'],
                        ['publish_intro', 'المقدّمة التعريفية', 'textarea'],
                      ].map(([field, label, kind]) => (
                        <div key={field}>
                          <label className="form-label" style={{ fontSize: 12 }}>{label}</label>
                          {kind === 'input' ? (
                            <input
                              dir="rtl" className="form-input" value={publishDraft[field]}
                              onChange={e => setPublishDraft(d => ({ ...d, [field]: e.target.value }))}
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <textarea
                              dir="rtl" className="form-textarea" value={publishDraft[field]}
                              onChange={e => setPublishDraft(d => ({ ...d, [field]: e.target.value }))}
                              style={{ width: '100%', minHeight: 80, lineHeight: 1.9, fontSize: 14 }}
                            />
                          )}
                        </div>
                      ))}
                      <div>
                        <label className="form-label" style={{ fontSize: 12 }}>الوسوم (تفصل بفاصلة)</label>
                        <input
                          dir="rtl" className="form-input" value={publishDraft.tags}
                          onChange={e => setPublishDraft(d => ({ ...d, tags: e.target.value }))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  ) : selected.publish_title || selected.publish_description || selected.publish_intro || (selected.publish_tags || []).length ? (
                    <div dir="rtl" style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                      {selected.publish_title && (
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{selected.publish_title}</div>
                      )}
                      {selected.publish_description && (
                        <div style={{ color: 'var(--text-muted)', lineHeight: 1.9 }}>{selected.publish_description}</div>
                      )}
                      {selected.publish_intro && (
                        <div style={{ lineHeight: 1.9, whiteSpace: 'pre-wrap', padding: 10, borderRadius: 6, background: 'var(--gray-50, var(--surface-alt))' }}>
                          {selected.publish_intro}
                        </div>
                      )}
                      {(selected.publish_tags || []).length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {selected.publish_tags.map((t, i) => (
                            <span key={i} style={{
                              fontSize: 12, padding: '2px 8px', borderRadius: 12,
                              background: 'var(--c-blue-100)', color: 'var(--c-blue-500)',
                            }}>{t}</span>
                          ))}
                        </div>
                      )}
                      {(selected.publish_headings || []).length > 0 && (
                        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                            العناوين الفرعية ({selected.publish_headings.length}) — كلٌّ قبل فقرته
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {selected.publish_headings.map((h, i) => (
                              <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                                <span style={{
                                  fontFamily: 'monospace', fontSize: 11, minWidth: 52, textAlign: 'center',
                                  background: 'var(--gray-100, var(--surface-alt))', borderRadius: 4, padding: '2px 4px',
                                }}>
                                  فقرة {h.after_paragraph + 1}
                                </span>
                                <span>{h.title}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لا حقول نشر بعد — اضغط "🔄 توليد" لاقتراح العنوان والوصف والمقدّمة والوسوم
                    </div>
                  )}
                  {selected.published_at && (
                    <div style={{
                      marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12,
                      background: 'var(--c-blue-100)', color: 'var(--c-blue-500)',
                      display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                    }}>
                      <span>🌐 نُشر {new Date(selected.published_at).toLocaleString('ar-SA')}</span>
                      {selected.published_url && (
                        <a href={selected.published_url} target="_blank" rel="noreferrer">فتح على الموقع ↗</a>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                    💡 المقترَح لا يُنشر كما هو — راجعه وعدّله قبل النشر باسم الشيخ
                  </div>
                </div>
              )}

              {/* النسخة المقروءة — البند ٢١: تُحفظ بجانب الحرفية لا مكانها */}
              {selected.status === 'completed' && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>📖 النسخة المقروءة</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {readableEdit ? (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={saveReadable} disabled={readableBusy}>💾 حفظ</button>
                          <button className="btn btn-sm btn-outline" onClick={() => setReadableEdit(false)}>إلغاء</button>
                        </>
                      ) : (
                        <>
                          {selected.readable_text && (
                            <button className="btn btn-sm btn-outline" onClick={() => {
                              navigator.clipboard.writeText(selected.readable_text);
                              toast.success('نُسخت النسخة المقروءة');
                            }}>📋 نسخ</button>
                          )}
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => { setReadableDraft(selected.readable_text || ''); setReadableEdit(true); }}
                            disabled={!selected.readable_text}
                          >✏️ تعديل</button>
                          <button className="btn btn-sm btn-outline" onClick={regenReadable} disabled={readableBusy}>
                            {readableBusy ? '⏳ جارٍ' : '🔄 توليد'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {readableEdit ? (
                    <textarea
                      dir="rtl" className="form-textarea" value={readableDraft}
                      onChange={e => setReadableDraft(e.target.value)}
                      style={{ width: '100%', minHeight: 300, lineHeight: 2, fontSize: 14 }}
                    />
                  ) : selected.readable_text ? (
                    <div dir="rtl" style={{ fontSize: 14, lineHeight: 2, whiteSpace: 'pre-wrap', maxHeight: 420, overflowY: 'auto' }}>
                      {selected.readable_text}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                      لا نسخة مقروءة بعد — اضغط "🔄 توليد" لحذف الحشو الشفهي والتكرار
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                    💡 النصّ الحرفيّ فوق باقٍ كما هو — الأرشيف يحتاجه للأمانة، والموقع يحتاج هذه للقارئ
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* البند ٢٢: النشر باسم الشيخ لا يكون صندوقاً مغلقاً — يُعرض ما سيُرسل */}
      {publishTo && (
        <div className="modal-overlay" onClick={() => setPublishTo(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} dir="rtl">
            <div className="modal-title">🌐 النشر إلى موقع الشيخ</div>

            {publishTo.blockers.length > 0 ? (
              <div className="card" style={{ background: 'var(--c-coral-soft)', fontSize: 13, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>لا يمكن النشر بعد:</div>
                <ul style={{ margin: 0, paddingRight: 18, lineHeight: 1.9 }}>
                  {publishTo.blockers.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                يُرسَل إلى الموقع بحالة «{publishTo.payload.status === 'publish' ? 'منشور' : 'مسوّدة'}»
                {publishTo.published?.at && ' — وهذا تحديث للمقال المنشور لا مقال جديد'}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '50vh', overflowY: 'auto' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{publishTo.payload.title || '—'}</div>
              {publishTo.payload.excerpt && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{publishTo.payload.excerpt}</div>
              )}
              <div
                style={{
                  fontSize: 13, lineHeight: 1.9, padding: 12, borderRadius: 6,
                  background: 'var(--gray-50, var(--surface-alt))',
                }}
                dangerouslySetInnerHTML={{ __html: publishTo.payload.content || '' }}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={sendToSite}
                disabled={publishSending || publishTo.blockers.length > 0}
              >
                {publishSending ? '⏳ جارٍ الإرسال' : '🌐 أرسل الآن'}
              </button>
              <button className="btn btn-outline" onClick={() => setPublishTo(null)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
